"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { BookOpen, Heart, ShieldCheck, Sparkles, Star, Sun } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function OurGreenMission() {
  const headerReveal = useRevealOnScroll<HTMLDivElement>();
  const contentReveal = useRevealOnScroll<HTMLDivElement>();
  const statsReveal = useRevealOnScroll<HTMLDivElement>();
  const stats = [{
    icon: "book",
    number: "100%",
    label: "Water-Based Inks",
    bg: "bg-primary/20",
    iconColor: "text-primary",
    rotation: "rotate-0"
  }, {
    icon: "sun",
    number: "0%",
    label: "Wasteful Byproducts",
    bg: "bg-secondary/30",
    iconColor: "text-secondary",
    rotation: "rotate-0"
  }, {
    icon: "heart",
    number: "OEKO",
    label: "Certified Materials",
    bg: "bg-primary/20",
    iconColor: "text-primary",
    rotation: "rotate-0"
  }, {
    icon: "star",
    number: "24h",
    label: "Fast-Dry Efficiency",
    bg: "bg-primary/20",
    iconColor: "text-primary",
    rotation: "rotate-0"
  }];
  const teachers = [{
    name: "Alex",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720440/img/team_member_one.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720440/img/team_member_one.png",
    sizes: "100vw"
  }, {
    name: "Jordan",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720440/img/team_member_two.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720440/img/team_member_two.webp",
    sizes: "100vw"
  }, {
    name: "Taylor",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720440/img/team_member_three.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720440/img/team_member_three.webp",
    sizes: "100vw"
  }];
  const renderIcon = (icon: string, className: string) => {
    if (icon === "book") return <BookOpen className={className} />;
    if (icon === "sun") return <Sun className={className} />;
    if (icon === "heart") return <Heart className={className} fill="currentColor" />;
    return <Star className={className} fill="currentColor" />;
  };
  return <section data-section-id="319" id="our-green-mission" className="relative overflow-hidden bg-background py-20 md:py-28 lg:py-[7rem] text-foreground">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute top-20 left-1/2 -translate-x-1/2 h-[500px] w-[500px] rounded-lg bg-primary/5 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        {}
        <div ref={headerReveal.ref} className={`text-center transition-all duration-700 ease-out ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="inline-flex items-center gap-2 font-mono text-xs md:text-sm uppercase tracking-[0.25em] text-primary">
            <span>Sustainability</span>
            <Sparkles className="size-3.5" />
          </div>

          <h2 className="font-default mt-6 text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tighter text-foreground">
            Precision printing,
            <br />
            <span className="italic font-light text-primary">conscious</span>{" "}
            <span className="italic font-light">production.</span>
          </h2>
        </div>

        {}
        <div ref={contentReveal.ref} className={`mt-16 md:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 transition-all duration-700 ease-out ${contentReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {}
          <div className="lg:col-start-2 lg:col-span-6">
            <p className="font-default text-xl md:text-2xl leading-relaxed text-foreground/85">At GREAT STATE DTF we bridge the gap between high-speed industrial output and environmental responsibility. Our specialized transfer technology utilizes eco-friendly, water-based inks that deliver vibrant, durable results without the chemical footprint of traditional methods. We are committed to a cleaner future for apparel manufacturing, one print at a time.</p>
          </div>

          {}
          <div className="lg:col-start-9 lg:col-span-4 flex flex-col items-start lg:items-end justify-end gap-4">
            <div className="flex -space-x-3">
              {teachers.map((t, i) => <SafeHydrate><Avatar key={i} data-index={i} className="size-12 border-2 border-background shadow-md">
                  <AvatarImage src={t.src} alt={t.name} />
                  <AvatarFallback className="bg-accent text-accent-foreground font-default text-sm">
                    {t.name.charAt(0)}
                  </AvatarFallback>
                </Avatar></SafeHydrate>)}
            </div>
            <p className="font-default italic text-lg md:text-xl text-foreground/70 lg:text-right">
              Committed to quality,
              <br />
              <span className="text-primary">&nbsp;the GS team</span>
            </p>
          </div>
        </div>

        {}
        <div ref={statsReveal.ref} className={`mt-16 md:mt-20 grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 transition-all duration-700 ease-out ${statsReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {stats.map((stat, i) => <div key={i} data-index={i} className={`group relative bg-card rounded-lg p-6 md:p-7 shadow-md hover:shadow-lg transition-all duration-300`}>
              <div className={`inline-flex items-center justify-center size-12 md:size-14 rounded-lg ${stat.bg} mb-5 shadow-sm`}>
                {renderIcon(stat.icon, `size-6 md:size-7 ${stat.iconColor}`)}
              </div>
              <div className="font-default text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-none">
                {stat.number}
              </div>
              <div className="mt-3 font-mono text-xs md:text-sm uppercase tracking-wider text-muted-foreground">
                {stat.label}
              </div>
            </div>)}
        </div>

        {}
        <div className="mt-20 md:mt-24 flex justify-center">
          <svg aria-hidden="true" viewBox="0 0 400 24" className="w-full max-w-md h-6 text-primary/40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 12 Q 25 2, 50 12 T 100 12 T 150 12 T 200 12 T 250 12 T 300 12 T 350 12 T 398 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
        </div>
      </div>
    </section>;
}